import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { destroy, initialize } from "redux-form";
import { cloneDeep } from "lodash";

import { Icon } from "rhombus-react";
import { useModalStack, objectToBase64EncodedString } from "utils/common";
import { getToken } from "utils/Token";
import useRefinedData from "hooks/useRefinedData";
import useFramingCheck from "hooks/useFramingCheck";
import FramingButton from "widgets/modals/versions/refinement/FramingButton";
import AdjustFramingBlade from "widgets/modals/versions/refinement/AdjustFramingBlade";
import { renderDisplayPlacement } from "widgets/modals/creatives/GenerateVersionsAI/utils";
import { clearActiveVariable, setImportVersionsRefinement } from "reducers/Refinements";
import {
	API_URL,
	TIA_DOMAIN,
	ALTIMAGE,
	DISPLAY_HTML_TAG,
	HTML_ONPAGE,
	HTML_STANDARD,
	HTML_EXPAND,
	NATIVE_IMAGE,
	PREVIEW_2_URL
} from "utils/constants";
import { EDIT_VERSION_BLADE_FORM } from "forms/list";

import "./VersionPreviewCard.css";

const isFtUtilsPreviewSupported = (adType) => {
	return [
		ALTIMAGE,
		DISPLAY_HTML_TAG,
		HTML_ONPAGE,
		HTML_STANDARD,
		HTML_EXPAND,
	].includes(adType);
};

export default function VersionPreviewCard(props) {
	const {
		libraryId,
		creative,
		version,
		ftUtilsId,
		jobId,
	} = props;

	const { id: creativeId, adType, width, height } = creative;
	const { name: versionName, id: versionId } = version;
	const dispatch = useDispatch();
	const { importVersionsRefinements } = useSelector(state => state.refinements);
	const [framesAdjusted, setFramesAdjusted] = useState(importVersionsRefinements?.[creativeId]?.[versionName]?.variableConfig);
	const accessToken = getToken();
	const isFtUtilsPreview = isFtUtilsPreviewSupported(adType);
	let { variableConfig } = version;
	const [initialVariableConfig] = useState(cloneDeep(variableConfig));

	if (isFtUtilsPreview && importVersionsRefinements?.[creativeId]?.[versionName]?.variableConfig) {
		// If there is custom refinement for this version, we need to use it
		variableConfig = importVersionsRefinements?.[creativeId]?.[versionName]?.variableConfig;
	}

	const commonModalProps = {
		allowFullScreen: true,
		isFullScreen: false,
		setAppTitle: false,
		type: "blade",
		xClose: true,
	};
	const [, { popModal, pushModal, clearStack }, ModalStackHead] = useModalStack({ commonModalProps });

	let showAdjustFramingModal = useFramingCheck({ adType }, variableConfig);

	// If we have a custom refinement for this asset and it is modified
	// or if initial import comes with canvas in cropMetadata
	// we need to set framesAdjusted to true
	useEffect(() => {
		if (importVersionsRefinements?.[creativeId]?.[versionName]?.variableConfig
			|| Object.values(initialVariableConfig ?? {}).some(variable => variable?.cropMetadata?.canvas)) {
			setFramesAdjusted(true);
		}
	}, [importVersionsRefinements?.[creativeId]?.[versionName]?.variableConfig]);

	// We need to force initial crop as API is sending original image
	const { isLoading: areRefinementsLoading, refinedDataMap } = useRefinedData(variableConfig, version.variableConfig, libraryId, !framesAdjusted, true);

	variableConfig = useMemo(() => {
		if (!isFtUtilsPreview) {
			return variableConfig;
		}

		if (!areRefinementsLoading && refinedDataMap.size) {
			// We need to clone variableConfig to avoid modifying
			// variables inside object as just spread will not be enough
			variableConfig = cloneDeep(variableConfig);
			refinedDataMap.forEach((value, key) => {
				if (!variableConfig[key].assetUrl) {
					variableConfig[key].assetUrl = variableConfig[key].prop;
				}
				variableConfig[key] = {
					...variableConfig[key],
					value: value.refinementDataUrl,
					prop: value.refinementDataUrl,
				};
			});
			return variableConfig;
		}

		if (areRefinementsLoading) {
			return null;
		}

		// If there is no custom refinement for this version, we need to use original variableConfig
		return variableConfig;
	}, [areRefinementsLoading, refinedDataMap, isFtUtilsPreview]);

	function closeModal() {
		dispatch(destroy(EDIT_VERSION_BLADE_FORM));
		clearStack();
	}

	function saveRefinement(values) {
		// Before saving we need to round canvas dimensions to avoid
		// API error which expects integer values
		for (const key in values) {
			if (values[key].type !== "Image") {
				continue;
			}

			const canvas = values[key].cropMetadata?.canvas;
			if (canvas?.width) {
				values[key].cropMetadata.canvas = {
					top: Math.round(canvas.top),
					left: Math.round(canvas.left),
					width: Math.round(canvas.width),
					height: Math.round(canvas.height),
				};
			}
		}
		dispatch(setImportVersionsRefinement(creativeId,
			versionName,
			{ name: versionName, id: versionId, variableConfig: values }));
	}

	const openAdjustFramingModal = () => {
		// We need to initialize EDIT_VERSION_BLADE_FORM as popped blade is fully based on it
		dispatch(initialize(EDIT_VERSION_BLADE_FORM, variableConfig));
		dispatch(clearActiveVariable());

		function Configured(props) {
			let modalProps = {
				libraryId,
				hidePreviewButton: true,
				versionInfo: {
					name: versionName,
					variableConfig,
				},
				commonModalProps: { ...commonModalProps, closeModal },
				navigationActions: { popModal, pushModal },
				applyUpdates: saveRefinement,
			};

			return <AdjustFramingBlade {...modalProps} />;
		}

		pushModal(Configured);
	};

	// We need to render cropped image if one exists
	const previewData = useMemo(() => {
		if (!isFtUtilsPreview || !variableConfig) {
			return null;
		}

		const clickTags = [];
		for(let i = 1; i <= creative.numClickTags; i++){
			const clickTag = variableConfig[`clicktag${i}`]?.data;

			const clickTagRedirect = `${PREVIEW_2_URL}/clicktag/?count=${creative.numClickTags}&current=${i}`;

			if(clickTag){
				clickTags.push(`${clickTagRedirect}&DYNCTTEST=`); /*
					When it's processing `landingPageRedirects`, ftUtils appends the version's clicktag landing page
					to this URL if it exists, so we leave the `DYNCTTEST=` on the end to be completed by ftUtils
					which previews the clicktag landing page when the preview is clicked and the clicktag screen is shown.
				*/
			} else {
				clickTags.push(clickTagRedirect);
			}
		}

		const creativeData = {
			creative: {
				...creative,
				creativeLibrary: { id: libraryId },
				version: { ...variableConfig },
				baseAsset: {
					host: API_URL,
					path: `/crm/v1/asset-manager/${libraryId}/${creative.id}/`,
					file: creative.routeFile ? `${creative.routeFile}?download=false` : "main.html?download=false",
				},
			},
			placement: { id: ftUtilsId },
			landingPageRedirects: clickTags,
		};

		return creativeData;
	}, [variableConfig, ftUtilsId]);

	function reloadPreview() {
		// Only render when there is preview data
		// This is to prevent the preview from rendering
		// before cropping is complete
		if (previewData) {
			renderDisplayPlacement(previewData);
		}
	}

	useEffect(reloadPreview, [previewData]);

	const legacyPreview = useMemo(() => {
		if (isFtUtilsPreview) {
			return null;
		}
		const framePrereqs = [
			creativeId,
			variableConfig,
			accessToken,
		];

		if (framePrereqs.every(item => item)) {
			// Process the variableConfig to update props
			const processedVariableConfig = cloneDeep(variableConfig);
			Object.values(processedVariableConfig).forEach((variable) => {
				if (variable.type === "Text") {
					variable.value = variable.data;
				}
				if (variable.type === "Image") {
					if (adType === NATIVE_IMAGE) {
						variable.prop = `/${libraryId}/instantAssets/${variable.data}`;
					} else {
						if (variable.prop.indexOf("asset-manager") < 0) {
							variable.value = `${API_URL}/crm/v1/asset-manager/${libraryId}/images/instantAssets/${variable.data}`;
						}
					}
				}
				if (variable.type === "Video") {
					if (adType === NATIVE_IMAGE) {
						variable.value = `/${libraryId}/instantAssets/${variable.data}`;
					} else {
						if (variable.prop.indexOf("asset-manager") < 0) {
							// For videos value is used instead of data
							variable.value = `${API_URL}/crm/v1/asset-manager/${libraryId}/videos/instantAssets/${variable.data}`;
						}
					}
				}
			});

			const encodedString = objectToBase64EncodedString(processedVariableConfig);
			const dataUrl = `data:application/json;base64,${encodedString}`;

			const frameURL = `${TIA_DOMAIN}/basic/${creativeId}?dynamicAdsUrl=${dataUrl}&access_token=${accessToken}`;

			// We do not want scrollbars to appear in cards - iframe is set to creative size
			// and sometimes bars appear when new images or videos have slight deviations from proper size
			return <iframe width={width} height={height} scrolling="no" src={frameURL} />;
		}
		return null;
	}, [creativeId, jobId, variableConfig, accessToken, isFtUtilsPreview]);

	const renderPreview = () => {
		if (isFtUtilsPreview) {
			return (<figure className="iframe-container cypress-version-preview-content" style={{ width: `${width}px`, height: `${height}px` }}>
				{<div id={`ftscript_m${ftUtilsId}`} />}
			</figure>);
		} else {
			return legacyPreview;
		}
	};

	return (
		<>
			{ModalStackHead && <ModalStackHead />}
			<article className="version-preview-card">
				{renderPreview()}
				<p className="subheader">{versionName}</p>
				{versionId && <p className="subheader">{versionId}</p>}
				<footer className="preview-controls cypress-variation-preview-actions">
					<Icon
						name="far fa-sync-alt"
						className="version-refresh"
						onClick={reloadPreview} />
					<FramingButton isVisible={showAdjustFramingModal} onClick={openAdjustFramingModal} />
				</footer>
			</article>
		</>
	);
}

VersionPreviewCard.displayName = "VersionPreviewCard";
